import { useEffect, useRef, type FormEvent, type ReactNode, type RefObject } from 'react';

/**
 * Shared compose surface — the A+ text-entry primitive (card vUak8P9oPODW).
 *
 * One writing component feeds EVERY free-text surface — Thread composer,
 * question answers, the request composer, Notebook quick-add — while each
 * screen keeps its OWN routing, form, API endpoint and audience/visibility
 * state (founder: "feel, not flow"). What it owns for all of them:
 *
 *   - a textarea that grows with the draft (scrollHeight -> inline height,
 *     40vh CSS cap — the same JS half card CwbqiUPwRg02 shipped), because
 *     field-sizing: content alone is the half the founder's build failed on
 *   - Enter BREAKS THE LINE by default (founder email 2026-10-21: "text entry
 *     fields should default to enter as line break rather than submit — IE if
 *     user wants to make a long entry"). The send button submits. A surface
 *     that genuinely wants Enter to send passes enterBehavior='submit'.
 *     Shift+Enter always breaks, both IME-safe
 *   - a width/wrap reflow pass (ResizeObserver) so a rotation or a keyboard
 *     inset re-wraps the box instead of leaving it at its old tall height
 *   - an optional controls row beneath the field — the surface passes its own
 *     controls as children (the + compose toggle, the concierge chip, the
 *     save-to-notebook toggle) and the submit button rides the end of the row
 *   - disabled / busy states
 *
 * Two render shapes:
 *   - default: owns the whole <form> (placeholder/aria/rows/maxLength/label/
 *     controls/send). The surfaces that ARE their own form — Thread, the
 *     question panel — pass their surface class and controls.
 *   - `bare`: renders ONLY the label + field, with no <form> / controls /
 *     submit — for surfaces that already own a surrounding <form> (Requests
 *     has templates + repeat chips, Notebook quick-add has its visibility
 *     row). The parent form's submit button drives submission; Enter still
 *     sends via requestSubmit() on the enclosing form; Shift+Enter breaks.
 *
 * Draft persistence is the owning screen's decision, never this component's:
 * this one is controlled — it only renders what it is given. Per-entity draft
 * keys and acked-snapshot clearing stay in the surfaces that persist text.
 */
interface ComposerProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  /** Submit handler for the <form> this component owns (default/non-bare
   *  shape). Optional because `bare` mode renders no form of its own and the
   *  surface's own form handles submission. */
  onSubmit?: (e: FormEvent) => void;
  placeholder?: string;
  ariaLabel?: string;
  maxLength?: number;
  rows?: number;
  disabled?: boolean;
  /** Enter key behaviour. Default 'newline' (founder email 2026-10-21): Enter
   *  breaks the line and the surface's send button submits, so a long entry
   *  can be written without firing early. 'submit' sends the surrounding form
   *  (opt-in per surface). Both keep Shift+Enter as a hard newline, both are
   *  IME-safe (Enter commits a composition candidate instead of firing). */
  enterBehavior?: 'submit' | 'newline';
  /** Class placed on the <form> so each surface keeps its own box. */
  formClassName?: string;
  /** Optional id on the <form> (walk-hints clicks #chat-form's submit). */
  formId?: string;
  /** Class on the <textarea>. */
  inputClassName?: string;
  /** data-test on the <textarea> (Notebook's quick-add keeps nb-quick-input). */
  dataTest?: string;
  /** Autofocus the field on mount (Notebook's quick-add composer). */
  autoFocus?: boolean;
  /** Optional ref to the <textarea> (the Thread refocuses it after a compose-pick). */
  textAreaRef?: RefObject<HTMLTextAreaElement | null>;
  /** Optional label rendered above the field (the question panel's "Your answer"). */
  label?: string;
  /** Controls rendered on their own row beneath the field, before send. */
  controls?: ReactNode;
  controlsClassName?: string;
  /** Send button affordance. */
  sendLabel?: string;
  sendAriaLabel?: string;
  sendClassName?: string;
  sendDisabled?: boolean;
  /** Render only label + field (no <form>/controls/submit) for surfaces that
   *  already own a surrounding <form>. */
  bare?: boolean;
}

export function Composer({
  id,
  value,
  onChange,
  onSubmit,
  placeholder,
  ariaLabel,
  maxLength,
  rows = 2,
  disabled = false,
  enterBehavior = 'newline',
  formClassName,
  formId,
  inputClassName,
  dataTest,
  autoFocus = false,
  textAreaRef,
  label,
  controls,
  controlsClassName = 'pear-composer-controls',
  sendLabel = '→',
  sendAriaLabel = 'Send',
  sendClassName,
  sendDisabled = false,
  bare = false,
}: ComposerProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const field = () => inputRef.current ?? textAreaRef?.current ?? null;

  /* Grow the box with the draft (same half as the Thread composer, card
     CwbqiUPwRg02): the inline height is set from scrollHeight and the CSS
     keeps the floors — min-height and the 40vh cap (max-height still wins
     over this inline height). */
  const grow = () => {
    const el = field();
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => { grow(); }, [value, textAreaRef]);

  /* Grow on FOCUS too (founder email 2026-10-21: "the thread text entry
     scrolls correctly but doesn't resize to fit the space available when
     clicked into"). Clicking in is the moment the keyboard opens and the
     available height changes, and nothing else fires then — value has not
     moved and the container width usually has not either. */

  /* A+ width/wrap reflow: `grow` above recomputes only when VALUE changes, so
     a width change that re-wraps the text (device rotation, a keyboard inset
     settling, the window resized) left the box at its old tall height. Watch
     the FIELD'S CONTAINER — its width is what re-wraps — and re-grow only
     when that width actually moved. Never observe the field itself: setting
     its height would fire the observer and loop. */
  useEffect(() => {
    const el = field();
    if (!el || typeof ResizeObserver === 'undefined') return;
    const host = el.parentElement;
    if (!host) return;
    let lastW = -1;
    const ro = new ResizeObserver(() => {
      const w = host.getBoundingClientRect().width;
      if (Math.abs(w - lastW) > 1) { lastW = w; grow(); }
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [textAreaRef]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // IME composition keeps Enter (it commits the candidate).
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    if (enterBehavior === 'newline') return; // let the field break the line
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  };

  const fieldEl = (
    <textarea
      ref={textAreaRef ?? inputRef}
      id={id}
      className={inputClassName}
      data-test={dataTest}
      autoFocus={autoFocus}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={value}
      rows={rows}
      maxLength={maxLength}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => grow()}
      onKeyDown={onKeyDown}
    />
  );

  if (bare) {
    return (
      <>
        {label && (
          <label htmlFor={id}>
            {label}
          </label>
        )}
        {fieldEl}
      </>
    );
  }

  return (
    <form id={formId} className={formClassName} onSubmit={onSubmit}>
      {label && (
        <label htmlFor={id}>
          {label}
        </label>
      )}
      {fieldEl}
      {/* The controls row ALWAYS renders. It used to be conditional on
          `controls || !sendDisabled`, which was written when every surface
          passed custom controls — the question panel then dropped its
          save-to-notebook toggle (founder email 2026-10-19) and the row
          vanished exactly when it was disabled, taking the submit button and
          its status copy ("Locked in — opens when they answer…") with it. A
          disabled submit is the honest render: the reason stays on screen. */}
      <div className={controlsClassName}>
        {controls}
        <button
          type="submit"
          className={sendClassName}
          aria-label={sendAriaLabel}
          disabled={sendDisabled}
        >
          {sendLabel}
        </button>
      </div>
    </form>
  );
}
